import axios from "axios";

// Always use current origin — works on preview, deployed, and custom domains
const API = `${window.location.origin}/api`;

// Dedicated axios instance for our API so interceptors don't leak to other axios usage.
const apiClient = axios.create({ baseURL: API });

/** DRW-LOAD-C §1 — this client had NO timeout at all, so one stalled object-storage read (measured
 *  at 28s against 0.2s normals) left the request hanging and everything behind it with it. Reads
 *  now get a few seconds and two retries with exponential backoff; writes get longer and are never
 *  retried automatically (a write with no answer may already have landed). */
const READ_TIMEOUT_MS = 8000;
const WRITE_TIMEOUT_MS = 20000;
const RETRY_DELAYS_MS = [400, 1200];
const noAnswer = (e) => !e.response && (e.code === "ECONNABORTED" || e.code === "ETIMEDOUT"
  || e.code === "ERR_NETWORK" || /timeout|network/i.test(e.message || ""));

apiClient.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (config.timeout === undefined || config.timeout === 0) {
    config.timeout = method === "get" ? READ_TIMEOUT_MS : WRITE_TIMEOUT_MS;
  }
  return config;
});

// Request interceptor: attach JWT.
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: on 401, clear token and bounce to login.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem("auth_token");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login?error=session_expired";
      }
      return Promise.reject(error);
    }
    const cfg = error?.config || {};
    const attempt = cfg._attempt || 1;
    if ((cfg.method || "get").toLowerCase() === "get" && noAnswer(error)
        && attempt <= RETRY_DELAYS_MS.length && !cfg.signal?.aborted) {
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt - 1]));
      return apiClient.request({ ...cfg, _attempt: attempt + 1 });
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Recently viewed jobs / bids (per person, shown in Quick Search)
  getRecent: () => apiClient.get(`/me/recent`).then(r => r.data),
  addRecent: (item) => apiClient.post(`/me/recent`, item).then(r => r.data),
  getPeople: () => apiClient.get(`/people`).then(r => r.data),
  updatePerson: (email, data) => apiClient.put(`/people/${encodeURIComponent(email)}`, data).then(r => r.data),
  // Projects
  getProjects: (archived = false) => apiClient.get(`/projects`, { params: { archived } }).then(r => r.data),
  searchProjects: (params = {}, signal) => apiClient.get(`/projects/search`, { params, signal }).then(r => r.data),
  createProject: (data) => apiClient.post(`/projects`, data).then(r => r.data),
  addProjectNote: (id, text) => apiClient.post(`/projects/${id}/notes`, { text }).then(r => r.data),
  updateProject: (id, data) => apiClient.put(`/projects/${id}`, data).then(r => r.data),
  updateProjectOverwrites: (id, data) => apiClient.patch(`/projects/${id}/overwrites`, data).then(r => r.data),
  batchArchiveProjects: (ids) => apiClient.post(`/projects/batch-archive`, { ids }).then(r => r.data),
  unlockProject: (id) => apiClient.patch(`/projects/${id}/unlock`).then(r => r.data),
  assignJobNumber: (id) => apiClient.post(`/projects/${id}/assign-job-number`).then(r => r.data),
  unarchiveProject: (id) => apiClient.put(`/projects/${id}/unarchive`).then(r => r.data),
  deleteProject: (id) => apiClient.delete(`/projects/${id}`).then(r => r.data),
  getStatusOptions: () => apiClient.get(`/status-options`).then(r => r.data),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    // Don't set Content-Type — axios derives it (with the multipart boundary) from FormData.
    return apiClient.post(`/projects/import-excel`, formData).then(r => r.data);
  },

  // Contracts
  getContracts: (archived = false) => apiClient.get(`/contracts`, { params: { archived } }).then(r => r.data),
  createContract: (data) => apiClient.post(`/contracts`, data).then(r => r.data),
  updateContract: (id, data) => apiClient.put(`/contracts/${id}`, data).then(r => r.data),
  addContractNote: (id, text) => apiClient.post(`/contracts/${id}/notes`, { text }).then(r => r.data),
  unarchiveContract: (id) => apiClient.put(`/contracts/${id}/unarchive`).then(r => r.data),
  deleteContract: (id) => apiClient.delete(`/contracts/${id}`).then(r => r.data),
  // Analytics
  getHitRates: (am = "ALL", est = "ALL") => apiClient.get(`/analytics/hit-rates`, { params: { am, est } }).then(r => r.data),
  getMonthlyBookings: (am = "ALL", est = "ALL") => apiClient.get(`/analytics/monthly-bookings`, { params: { am, est } }).then(r => r.data),
  setMonthlyGoal: (goal) => apiClient.put(`/analytics/monthly-bookings/goal`, { goal }).then(r => r.data),
  getTop10: (am = "ALL", est = "ALL") => apiClient.get(`/analytics/top10`, { params: { am, est } }).then(r => r.data),
  getQuarterPipeline: (fy = "this", quarter = 1) => apiClient.get(`/analytics/quarter-pipeline`, { params: { fy, quarter } }).then(r => r.data),
  getRevenueForecast: () => apiClient.get(`/analytics/revenue-forecast`).then(r => r.data),
  getRevenueBid: () => apiClient.get(`/analytics/revenue-bid`).then(r => r.data),
  getRevenueBooked: () => apiClient.get(`/analytics/revenue-booked`).then(r => r.data),
  exportRevenueBooked: (start_date, end_date) =>
    apiClient.get(`/analytics/revenue-booked/export`, { params: { start_date, end_date }, responseType: "blob" })
      .then(r => r.data),

  // Accounts
  getAccounts: (assigned) => apiClient.get(`/accounts`, { params: assigned !== undefined ? { assigned } : {} }).then(r => r.data),
  createAccount: (data) => apiClient.post(`/accounts`, data).then(r => r.data),
  updateAccount: (id, data) => apiClient.put(`/accounts/${id}`, data).then(r => r.data),
  assignAccount: (id) => apiClient.put(`/accounts/${id}/assign`).then(r => r.data),
  unassignAccount: (id) => apiClient.put(`/accounts/${id}/unassign`).then(r => r.data),
  deleteAccount: (id) => apiClient.delete(`/accounts/${id}`).then(r => r.data),

  // No Bid List
  getNoBidList: () => apiClient.get(`/no-bid-list`).then(r => r.data),
  createNoBid: (data) => apiClient.post(`/no-bid-list`, data).then(r => r.data),
  updateNoBid: (id, data) => apiClient.put(`/no-bid-list/${id}`, data).then(r => r.data),
  deleteNoBid: (id) => apiClient.delete(`/no-bid-list/${id}`).then(r => r.data),

  // Reconcile
  getReconcile: (archived) => apiClient.get(`/reconcile`, { params: archived !== undefined ? { archived } : {} }).then(r => r.data),
  createReconcile: (data) => apiClient.post(`/reconcile`, data).then(r => r.data),
  updateReconcile: (id, data) => apiClient.put(`/reconcile/${id}`, data).then(r => r.data),
  unarchiveReconcile: (id) => apiClient.put(`/reconcile/${id}/unarchive`).then(r => r.data),
  deleteReconcile: (id) => apiClient.delete(`/reconcile/${id}`).then(r => r.data),

  // Account Managers
  getAccountManagers: () => apiClient.get(`/account-managers`).then(r => r.data),
  addAccountManager: (name) => apiClient.post(`/account-managers`, { name }).then(r => r.data),
  deleteAccountManager: (id) => apiClient.delete(`/account-managers/${id}`).then(r => r.data),


  // Audit Logs
  getAuditLogs: (params = {}) => apiClient.get(`/audit-logs`, { params }).then(r => r.data),

  // ABS Revenue Summary
  getAbsRevenue: () => apiClient.get(`/analytics/abs-revenue`).then(r => r.data),

  // Email Automation
  getEmailSettings: () => apiClient.get(`/email/settings`).then(r => r.data),
  updateEmailSettings: (data) => apiClient.put(`/email/settings`, data).then(r => r.data),
  previewReminder: (contractId) => apiClient.post(`/email/preview/${contractId}`).then(r => r.data),
  sendReminder: (contractId, data = {}) => apiClient.post(`/email/send-reminder/${contractId}`, data).then(r => r.data),
  getReminderLog: (contractId) => apiClient.get(`/email/reminder-log/${contractId}`).then(r => r.data),
  runSchedulerTick: () => apiClient.post(`/email/run-tick`).then(r => r.data),

  // Company Forecast
  getCompanyForecast: () => apiClient.get(`/forecast/company`).then(r => r.data),
  uploadBacklog: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(`/forecast/upload-backlog`, formData).then(r => r.data);
  },
  updateForecastSettings: (data) => apiClient.put(`/forecast/settings`, data).then(r => r.data),
  resetForecastSettings: () => apiClient.post(`/forecast/settings/reset`).then(r => r.data),
  exportForecast: (view) => apiClient.get(`/forecast/export`, { params: { view }, responseType: "blob" }).then(r => r.data),
  revertLastForecastSettings: () => apiClient.post(`/forecast/settings/revert-last`).then(r => r.data),
  getBacklogSnapshots: () => apiClient.get(`/forecast/snapshots`).then(r => r.data),
  uploadActuals: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(`/forecast/actuals/upload`, formData).then(r => r.data);
  },
  getForecastComparison: () => apiClient.get(`/forecast/comparison`).then(r => r.data),
  getForecastChangelog: () => apiClient.get(`/forecast/changelog`).then(r => r.data),
  undoForecastChange: (id) => apiClient.post(`/forecast/changelog/${id}/undo`).then(r => r.data),

  // Users, roles & permissions
  getUsers: () => apiClient.get(`/users`).then(r => r.data),
  inviteUser: (data) => apiClient.post(`/users`, data).then(r => r.data),
  updateUser: (email, data) => apiClient.put(`/users/${encodeURIComponent(email)}`, data).then(r => r.data),
  setUserGrants: (email, grants) => apiClient.put(`/users/${encodeURIComponent(email)}/grants`, { grants }).then(r => r.data),
  deleteUser: (email) => apiClient.delete(`/users/${encodeURIComponent(email)}`).then(r => r.data),
  getRoleAccess: () => apiClient.get(`/role-access`).then(r => r.data),
  saveRoleAccess: (access) => apiClient.put(`/role-access`, { access }).then(r => r.data),
  saveTabAccess: (tabs) => apiClient.put(`/role-access`, { tabs }).then(r => r.data),
};
